import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import React, { useContext, useEffect, useState } from 'react';
import { axiosGetCall, axiosDeleteCall } from '../../api/axiosCall';
import CommonLayout from '../../common/CommonLayout';
import DataTable from '../../common/DataTable';
import { getColumnSearchProps } from '../../common/DataTableSearchTemplate';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import AddPromotionalBanner from './AddPromotionalBanner';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';

const TableHeader = ({ handleAddButtonClick }) => {
  return (
    <div className='flex-row justify-end flex'>
      <Button
        type='primary'
        icon={<PlusOutlined />}
        className='border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
        onClick={handleAddButtonClick}
      >
        Add Promotional Banner
      </Button>
    </div>
  );
};

function PromotionalBanner() {
  const [promotionalBannerList, setPromotionalBannerList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { token } = useContext(AuthContext);

  useEffect(() => {
    getPromotionalBannerList();
  }, []);

  async function handlePromotionalBannerDelete(data) {
    Modal.confirm({
      title: 'Delete Promotional Banner',
      content: `Are you sure you want to delete the promotional banner "${data.promotionalBannerName}"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await axiosDeleteCall({
            apiEndPoint: `${API_END_POINT.DELETE_PROMOTIONAL_BANNER}?bannerId=${data.promotionalBannerId}`,
            header: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Check for successful deletion (handle different response structures)
          if (res.hideErrorFromUser) {
            message.success(
              res.data?.message ||
                res.message ||
                'Promotional banner deleted successfully',
            );
            // Refresh the banner list after successful deletion
            getPromotionalBannerList();
          } else {
            message.error(
              res.error?.message ||
                res.message ||
                'Failed to delete promotional banner',
            );
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error(SOMETHING_WENT_WRONG);
        }
      },
    });
  }

  const columns = [
    {
      title: 'Banner Image',
      width: 100,
      render: (record) => {
        return (
          <div className='h-16 w-16 rounded-full'>
            <img
              className={`rounded-full h-16 w-16 `}
              src={record.promotionalBannerImage}
              alt={record.promotionalBannerName}
            />
          </div>
        );
      },
    },
    {
      key: 'promotionalBannerName',
      title: 'Banner Name',
      dataIndex: 'promotionalBannerName',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a, b) => {
        if (a.promotionalBannerName)
          return a.promotionalBannerName.localeCompare(b.promotionalBannerName);
      },
      ...getColumnSearchProps('promotionalBannerName'),
    },
    {
      key: 'productName',
      title: 'Product Name',
      dataIndex: 'productName',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a, b) => {
        if (a.productName) return a.productName.localeCompare(b.productName);
      },
      ...getColumnSearchProps('productName'),
    },
    {
      key: 'startDate',
      title: 'Start Date',
      dataIndex: 'startDate',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a, b) => {
        if (a.startDate) return new Date(a.startDate) - new Date(b.startDate);
      },
      render: (date) => {
        return new Date(date).toLocaleDateString();
      },
    },
    {
      key: 'endDate',
      title: 'End Date',
      dataIndex: 'endDate',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a, b) => {
        if (a.endDate) return new Date(a.endDate) - new Date(b.endDate);
      },
      render: (date) => {
        return new Date(date).toLocaleDateString();
      },
    },
    {
      key: 'createdBy',
      title: 'Created By',
      dataIndex: 'createdBy',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a, b) => {
        if (a.createdBy) return a.createdBy.localeCompare(b.createdBy);
      },
      ...getColumnSearchProps('createdBy'),
    },
    {
      title: 'Actions',
      render: (e, record) => (
        <div className='flex items-center gap-2'>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              handlePromotionalBannerDelete(record);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  async function getPromotionalBannerList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PROMOTIONAL_BANNER_LIST,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(res);
      if (res.hideErrorFromUser) {
        // Handle both single object and array responses
        const bannerData = Array.isArray(res.data)
          ? res.data
          : res.data
            ? [res.data]
            : [];
        setPromotionalBannerList(bannerData);
      } else {
        message.error(SOMETHING_WENT_WRONG);
      }
      setIsLoading(false);
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  function handleAddButtonClick() {
    setIsModalOpen(!isModalOpen);
  }

  return (
    <CommonLayout>
      <DataTable
        title={<TableHeader handleAddButtonClick={handleAddButtonClick} />}
        isTableRowEditable={false}
        data={promotionalBannerList}
        columns={columns}
        isSpinning={isLoading}
        rowKey='promotionalBannerId'
      />
      <AddPromotionalBanner
        isOpen={isModalOpen}
        handleModelClose={handleAddButtonClick}
        callback={getPromotionalBannerList}
      />
    </CommonLayout>
  );
}

export default PromotionalBanner;
